// src/pages/Properties.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axiosInstance from "../utils/axiosInstance";

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [propertyTypes, setPropertyTypes] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    location: "",
    price: "",
    property_type: "",
    transaction_type: "rent",
    bedrooms: "",
    bathrooms: "",
    size: "",
    company_name: "",
    contact: "",
    verified: false,
    expiry_date: "",
    active: true,
    images: [],
    existingImages: [],
  });

  const fetchProperties = async () => {
    try {
      const res = await axiosInstance.get("/properties");
      setProperties(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPropertyTypes = async () => {
    try {
      const res = await axiosInstance.get("/properties/types");
      setPropertyTypes(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProperties();
    fetchPropertyTypes();
  }, []);

  const handleOpen = async (property = null) => {
    if (property) {
      setEditId(property.id);
      const res = await axiosInstance.get(`/properties/${property.id}`);
      const data = res.data;
      setForm({
        title: data.title || "",
        description: data.description || "",
        location: data.location || "",
        price: data.price || "",
        property_type: data.property_type || "",
        transaction_type: data.transaction_type || "rent",
        bedrooms: data.bedrooms || "",
        bathrooms: data.bathrooms || "",
        size: data.size || "",
        company_name: data.company_name || "",
        contact: data.contact || "",
        verified: !!data.verified,
        expiry_date: data.expiry_date ? data.expiry_date.split("T")[0] : "",
        active: data.active ?? true,
        images: [],
        existingImages: data.images || [],
      });
    } else {
      setEditId(null);
      setForm({
        title: "",
        description: "",
        location: "",
        price: "",
        property_type: "",
        transaction_type: "rent",
        bedrooms: "",
        bathrooms: "",
        size: "",
        company_name: "",
        contact: "",
        verified: false,
        expiry_date: "",
        active: true,
        images: [],
        existingImages: [],
      });
    }
    setOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      setForm((prev) => ({ ...prev, images: Array.from(files) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await axiosInstance.delete(`/properties/image/${imageId}`);
      setForm((prev) => ({
        ...prev,
        existingImages: prev.existingImages.filter((img) => img.id !== imageId),
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (key === "images") {
          form.images.forEach((file) => formData.append("images", file));
        } else {
          formData.append(key, form[key]);
        }
      });

      if (editId) {
        await axiosInstance.put(`/properties/${editId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axiosInstance.post("/properties", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setOpen(false);
      fetchProperties();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      await axiosInstance.delete(`/properties/${id}`);
      fetchProperties();
    } catch (err) {
      console.error(err);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 60 },
    { field: "title", headerName: "Title", flex: 1 },
    { field: "location", headerName: "Location", flex: 1 },
    { field: "price", headerName: "Price", width: 100 },
    { field: "property_type", headerName: "Type", width: 120 },
    { field: "transaction_type", headerName: "Transaction", width: 100 },
    {
      field: "active",
      headerName: "Active",
      width: 80,
      renderCell: (params) => (params.value ? "🟢" : "🔴"),
    },
    {
      field: "verified",
      headerName: "Verified",
      width: 80,
      renderCell: (params) => (params.value ? "✅" : "❌"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <>
          <Button size="small" onClick={() => handleOpen(params.row)}>Edit</Button>
          <Button size="small" color="error" onClick={() => handleDelete(params.row.id)}>Delete</Button>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <h2>Properties Management</h2>
      <Button variant="contained" color="primary" onClick={() => handleOpen()} sx={{ mb: 2 }}>
        Add Property
      </Button>

      <div style={{ height: 600, width: "100%", overflowX: "auto" }}>
        <DataGrid rows={properties} columns={columns} pageSize={5} rowsPerPageOptions={[5]} autoHeight />
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editId ? "Edit Property" : "Add Property"}</DialogTitle>
        <DialogContent>
          <TextField label="Title" name="title" value={form.title} onChange={handleChange} fullWidth margin="normal" required />
          <TextField label="Description" name="description" value={form.description} onChange={handleChange} fullWidth multiline rows={3} margin="normal" />
          <TextField label="Location" name="location" value={form.location} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Price" name="price" value={form.price} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Company / Realtor" name="company_name" value={form.company_name} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Contact" name="contact" value={form.contact} onChange={handleChange} fullWidth margin="normal" />

          <TextField select label="Property Type" name="property_type" value={form.property_type} onChange={handleChange} fullWidth margin="normal" SelectProps={{ native: true }}>
            <option value="">Select Type</option>
            {propertyTypes.map((type) => (
              <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
            ))}
          </TextField>

          <TextField select label="Transaction Type" name="transaction_type" value={form.transaction_type} onChange={handleChange} fullWidth margin="normal" SelectProps={{ native: true }}>
            <option value="rent">Rent</option>
            <option value="sale">Sale</option>
          </TextField>

          <TextField label="Bedrooms" name="bedrooms" value={form.bedrooms} onChange={handleChange} margin="normal" />
          <TextField label="Bathrooms" name="bathrooms" value={form.bathrooms} onChange={handleChange} margin="normal" />
          <TextField label="Size" name="size" value={form.size} onChange={handleChange} margin="normal" />
          <TextField label="Expiry Date" name="expiry_date" type="date" value={form.expiry_date} onChange={handleChange} fullWidth margin="normal" />

          <FormControlLabel control={<Switch checked={form.active} onChange={handleChange} name="active" />} label="Active" />
          <FormControlLabel control={<Switch checked={form.verified} onChange={handleChange} name="verified" />} label="Verified" />

          <label style={{ marginTop: 16, display: "block" }}>Main & Additional Images:</label>
          <input type="file" name="images" multiple onChange={handleChange} style={{ marginTop: 8 }} />
          {form.existingImages.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
              {form.existingImages.map((img) => (
                <div key={img.id} style={{ position: "relative" }}>
                  <img src={img.image_url} alt="Property" width={80} />
                  <Button size="small" color="error" onClick={() => handleDeleteImage(img.id)}>X</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>{editId ? "Update" : "Save"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Properties;
